import React, { useEffect, useRef, useState } from 'react';

/**
 * Thin, non-blocking top progress bar (YouTube / nprogress style).
 *
 * `active` drives it: while true the bar trickles toward ~90% (it can never
 * reach 100% on its own — we don't know the real ETA), and when `active`
 * flips false it snaps to 100% and fades out. It renders nothing while idle,
 * so it adds zero layout cost when there's nothing loading.
 *
 * Used to cover the Analytics fetch waterfall (participants → active users →
 * monthly stats) where the route is mounted but data hasn't landed — the
 * skeletons say *where* content will appear; this says *something is working*.
 */
export default function TopProgressBar({ active, className = '' }) {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (active) {
      setVisible(true);
      // Kick off from a small head-start so the bar is immediately visible,
      // unless a prior trickle is still mid-flight.
      setProgress((p) => (p > 0 && p < 90 ? p : 8));
      intervalRef.current = window.setInterval(() => {
        setProgress((p) => {
          if (p >= 90) return p;
          // Ease out: larger steps early, asymptotically approaching 90%.
          const inc = Math.max(0.5, (90 - p) * 0.08);
          return Math.min(90, p + inc);
        });
      }, 180);
      return () => window.clearInterval(intervalRef.current);
    }

    // Not active: if we were showing, complete then fade out.
    if (visible) {
      setProgress(100);
      const t = window.setTimeout(() => {
        setVisible(false);
        setProgress(0);
      }, 300);
      return () => window.clearTimeout(t);
    }
    return undefined;
    // `visible` intentionally omitted: we only react to `active` edges.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-x-0 top-0 z-[60] h-0.5 pointer-events-none ${className}`.trim()}
      role="progressbar"
      aria-hidden="true"
    >
      <div
        className="h-full bg-gradient-to-r from-brand-400 to-brand-600 transition-[width,opacity] duration-200 ease-out"
        style={{
          width: `${progress}%`,
          opacity: progress >= 100 ? 0 : 1,
          boxShadow: '0 0 8px rgba(29, 138, 155, 0.5)',
        }}
      />
    </div>
  );
}
